/**
 * The password form, used on an account that has no password.
 *
 * `User.passwordHash` is nullable so that an account created through Google is not given an
 * invented one. `POST /auth/login` therefore has a legitimate answer that is not "wrong
 * password", and `docs/SOCIAL_LOGIN.md` writes the sentence for it: *"This account signs in with
 * Google. Use Continue with Google, or set a password from the email we send you."*
 *
 * Both offers in that sentence are real here. The second one goes through
 * `POST /api/v1/auth/forgot`, which already mails a link that sets a password — the same
 * endpoint, doing the same job, for an account that never had one.
 */

import { useState } from 'react'
import type { ReactElement } from 'react'

import { errorMessage } from '../../../lib/api'
import { requestPasswordReset } from '../../../lib/auth'
import { useT } from '../../../lib/i18n'
import { Icon } from '../../../components/ui/Icon'
import { Spinner } from '../../../components/ui/Spinner'
import { passwordlessAccountCopy } from './copy'
import type { SocialProvider } from './contract'

export interface PasswordlessNoticeProps {
  /** The provider the server named, when it named one. */
  provider: SocialProvider | null
  /** The address typed into the form — the link goes there. */
  email: string
}

type Stage = 'idle' | 'sending' | 'sent' | 'failed'

export const PasswordlessNotice = ({ provider, email }: PasswordlessNoticeProps): ReactElement => {
  const t = useT()
  const [stage, setStage] = useState<Stage>('idle')
  const [failure, setFailure] = useState<string | null>(null)

  const send = async (): Promise<void> => {
    setStage('sending')
    setFailure(null)
    try {
      await requestPasswordReset(email)
      setStage('sent')
    } catch (error) {
      setFailure(errorMessage(error))
      setStage('failed')
    }
  }

  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-hair bg-brass-wash p-3"
    >
      <Icon name="lock" size={16} className="mt-0.5 shrink-0 text-brass-deep" />
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="t-small text-ink">{passwordlessAccountCopy(t, provider)}</p>

        {stage === 'sent' ? (
          <p className="t-small text-ink-mute">{t('auth.social.setPasswordSent', { email })}</p>
        ) : (
          <span className="flex items-center gap-2">
            <button
              type="button"
              disabled={stage === 'sending'}
              onClick={() => {
                void send()
              }}
              className="ring-focus t-small font-semibold text-brass-deep underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t('auth.social.setPasswordCta')}
            </button>
            {stage === 'sending' ? (
              <Spinner size="sm" label={t('auth.social.sendingLink')} />
            ) : null}
          </span>
        )}

        {stage === 'failed' && failure ? <p className="t-small text-down-deep">{failure}</p> : null}
      </div>
    </div>
  )
}
